// A player's gender on ?live=1 (T2-14), for the Stats / Radar tiles' drop rule: a man and a woman
// are never compared on one tile (Prateek, 2026-10-15). A drop is decided while the pointer moves,
// so it can't wait for a call: a live Stats tile notes each player's gender when it loads her
// competitions, a live Radar when it loads her league's file (T2-16b), a live Ranking / Squad row
// when it's dragged, and the rule reads it here. Like picked-players.ts, this is who she IS, kept
// for the page's life — no API answer is reused (that's Track 3). Plain code, no Amplify: the
// canvas imports it.

import type { PlayerCompetition } from "@catbento/shared";
import type { PlayerId } from "../data/players.js";
import { rememberPickedPlayer, type PickedPlayer } from "./picked-players.js";

const womens = new Map<PlayerId, boolean>();

/** Her gender from her competitions in a season: true = women's. Null when it can't be told —
 *  no flags, or men's and women's together (a data problem; the drop is then refused). Pure. */
export function womensFrom(competitions: readonly PlayerCompetition[]): boolean | null {
  const flags = new Set(competitions.map((c) => c.competitionWomens).filter((f): f is boolean => f !== null));
  return flags.size === 1 ? [...flags][0]! : null;
}

export function rememberWomens(id: PlayerId, isWomens: boolean | null): void {
  if (isWomens !== null) womens.set(id, isWomens);
}

/**
 * A player dragged out of a live list row (Ranking, Squad). The list already knows her gender
 * (its league's), so it's noted here: without it a Stats tile would refuse her until one had
 * loaded her. She's also remembered as a pick — the merge opens a module on her and its legend
 * names her, and a player the snapshot lacks has no name anywhere else.
 */
export function noteRowPlayer(p: PickedPlayer, isWomens: boolean | null, positionTemplate?: string | null): void {
  rememberPickedPlayer(p);
  rememberWomens(p.id, isWomens);
  rememberKeeper(p.id, positionTemplate);
}

/** Her gender if a live Stats / Radar tile or list row has noted it this page, else null. */
export function knownWomens(id: PlayerId): boolean | null {
  return womens.get(id) ?? null;
}

// Whether she's a goalkeeper (Ben, 2026-10-16: an outfield player must never be merged onto a
// Goalkeeping tile). Noted, like her gender, from her main position when a live tile or Bio loads
// her; the drop rule refuses her onto Goalkeeping until it's known.
const keepers = new Map<PlayerId, boolean>();

/** `positionTemplate` = her main position ("goalkeeper", "full_back"…); null = not known. */
export function rememberKeeper(id: PlayerId, positionTemplate: string | null | undefined): void {
  if (positionTemplate) keepers.set(id, positionTemplate === "goalkeeper");
}

/** Is she a goalkeeper, if a live tile has noted it this page; else null. */
export function knownKeeper(id: PlayerId): boolean | null {
  return keepers.get(id) ?? null;
}

// The season a tile opens a player on when nothing picks one (Ben, 2026-11-02: a group dropped on
// a Radar added Declan Rice a second time). A Radar or Stats tile records the season it shows
// ("52026"); a Bio dragged in carries none, meaning "her current one". Compared as they were, the
// two read as different seasons, a year-on-year. The tiles note what "current" resolved to here,
// and the drop rule reads it to put the two on the same footing.
const currentSeasons = new Map<PlayerId, string>();

export function rememberCurrentSeason(id: PlayerId, season: string): void {
  currentSeasons.set(id, season);
}

/** The season "her current one" opened on in a live tile this page, else null. */
export function knownCurrentSeason(id: PlayerId): string | null {
  return currentSeasons.get(id) ?? null;
}

/** For tests only. */
export function forgetWomens(): void {
  womens.clear();
  keepers.clear();
}
